/**
 * Native Cart Drawer — PWT Sitespeed
 * Vanilla JS, no jQuery dependency
 */
(function () {
  'use strict';

  var SELECTORS = {
    drawer: '#NativeCartDrawer',
    overlay: '.native-cart-overlay',
    panel: '.native-cart-panel',
    closeBtn: '[data-action="close-drawer"]',
    cartItems: '[data-cart-items]',
    cartCount: '[data-cart-count]',
    cartSubtotal: '[data-cart-subtotal]',
    cartDiscounts: '[data-cart-discounts]',
    rewardBar: '.reward-bar',
    rewardFill: '[data-reward-fill]',
    rewardMessage: '[data-reward-message]',
    rewardTicks: '[data-reward-tick]',
    rewardLabels: '[data-reward-label]',
    noteWrapper: '[data-cart-note-wrapper]',
    noteInput: '[data-cart-note-input]',
    headerBubble: '.cart-link__bubble',
    headerCartLinks: '.js-native-cart-open'
  };

  var REDO_PRODUCT_ID, REDO_VARIANT_ID;
  var REDO_ENABLED = false, UPSELL_ENABLED = false, NOTE_ENABLED = false;
  var UPSELL_COUNT = 4;
  var EXCLUDED_PRODUCT_IDS = [9814282502454];
  var TIERS = [];

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var ROUTES = {
    cart: ROOT + 'cart.js',
    cartAdd: ROOT + 'cart/add.js',
    cartChange: ROOT + 'cart/change.js',
    cartUpdate: ROOT + 'cart/update.js'
  };

  var drawer, isOpen = false, noteTimer = null, redoAddInProgress = false;

  // ─── Helpers ───────────────────────────────────────
  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return (root || document).querySelectorAll(sel); }

  function formatMoney(cents) {
    var format = (window.theme && window.theme.settings && window.theme.settings.moneyFormat) || '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    var amountNoDecimals = Math.floor(cents / 100);
    var amountWithComma = amount.replace('.', ',');

    return format
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amount);
  }

  function buildCodeBox(code) {
    return '<span class="reward-bar__code-box" data-copy-code="' + code + '" onclick="navigator.clipboard.writeText(this.dataset.copyCode);this.classList.add(\'is-copied\');var el=this;setTimeout(function(){el.classList.remove(\'is-copied\')},1500)" title="click to copy">' +
      '<span class="reward-bar__code-text">' + code + '</span>' +
      '<span class="reward-bar__code-copy">copy</span>' +
      '<span class="reward-bar__code-copied">copied!</span>' +
    '</span>';
  }

  // 1 Oct 2026: shoppers on slow mobile connections were seeing "Request timed out"
  // in the drawer. The 10s abort was shorter than a bad 5G/3G round trip to the cart
  // API, and an aborted POST has usually already been applied by Shopify, so the
  // drawer then showed an error for an add that had worked. Now: 30s budget, cart
  // reads retry once, timeouts are flagged so callers can reconcile against the
  // real cart, and the drawer still opens (its checkout form posts to /checkout
  // independently of this script) when the refresh fails.
  var REQUEST_TIMEOUT_MS = 30000;

  // 12 Oct 2026: every cart call goes through whatever has wrapped window.fetch
  // (Shopify's shop_events_listener, Redo's add watcher). On iPhones in the US
  // market the promise handed back by those wrappers sometimes never settled, so
  // the add sat on "adding..." for good, the form's in-flight flag never cleared
  // and every later tap on either button did nothing. The network abort above
  // cannot reach a promise a wrapper is holding, so two guards sit around it:
  //  - a settle timer (SETTLE_MS): past it the call counts as timed out whatever
  //    the wrapper is doing; reads are answered again through cleanFetch, writes
  //    are handed to the caller's reconcile path (recoverFailedAdd);
  //  - cleanFetch(): the browser's own fetch, taken in layout/theme.liquid before
  //    any app script runs, or from a blank same-origin frame. Once a wrapped
  //    call has stalled while a clean one answered, the rest of the visit uses
  //    the clean one. Redo only watches responses, so a clean add is followed by
  //    a wrapped cart read for it to see.
  var SETTLE_MS = 6000;
  var CLEAN_SETTLE_MS = 10000;
  var QUICK_MS = 2500;
  var preferClean = false;
  var _cleanFetch = null;

  function isNativeFn(f) {
    try { return typeof f === 'function' && /\[native code\]/.test(Function.prototype.toString.call(f)); } catch (e) { return false; }
  }

  function cleanFetch() {
    if (_cleanFetch) return _cleanFetch;
    var captured = window.__pwtFetch;
    if (isNativeFn(captured)) {
      _cleanFetch = function () { return captured.apply(window, arguments); };
      return _cleanFetch;
    }
    try {
      var frame = document.createElement('iframe');
      frame.setAttribute('aria-hidden', 'true');
      frame.tabIndex = -1;
      frame.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
      document.documentElement.appendChild(frame);
      var w = frame.contentWindow;
      if (w && isNativeFn(w.fetch)) {
        _cleanFetch = function () { return w.fetch.apply(w, arguments); };
        return _cleanFetch;
      }
    } catch (e) {}
    _cleanFetch = function () { return window.fetch.apply(window, arguments); };
    return _cleanFetch;
  }

  function goClean(why) {
    if (preferClean) return;
    preferClean = true;
    console.warn('[cart] ' + why + '; using the browser\'s own fetch for the rest of this visit');
  }

  function timeoutError(pending) {
    var err = { description: 'Request timed out. Please try again.', timedOut: true, transient: true };
    if (pending) err.pending = pending;
    return err;
  }

  function parseResponse(r) {
    return r.text().then(function (text) {
      var data;
      try { data = JSON.parse(text); } catch (e) {
        return Promise.reject({ description: 'Unexpected server response. Please try again.', status: r.status });
      }
      if (!r.ok) {
        if (!data.description) data.description = data.message || 'Something went wrong (' + r.status + ')';
        return Promise.reject(data);
      }
      return data;
    });
  }

  function normalizeError(err) {
    if (err && err.name === 'AbortError') return timeoutError();
    if (!err || !err.description) {
      // fetch() itself rejecting = network failure, not a cart error
      return { description: (err && err.message) || 'Something went wrong. Please try again.', transient: true };
    }
    return err;
  }

  // extra: { clean: true } forces the browser's own fetch; { settleMs } overrides
  // the settle timer. Errors carry .transient for callers that reconcile, and
  // .pending (the still-running request) when a wrapped write stalled.
  function fetchJSON(url, opts, extra) {
    opts = opts || {};
    extra = extra || {};
    opts.headers = opts.headers || {};
    opts.headers['Accept'] = 'application/json';
    var method = (opts.method || 'GET').toUpperCase();
    if (method === 'POST') {
      opts.headers['Content-Type'] = 'application/json';
    }
    var useClean = !!(extra.clean || preferClean);
    var settleMs = extra.settleMs || (useClean ? CLEAN_SETTLE_MS : SETTLE_MS);
    var controller = new AbortController();
    var timeoutId = setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT_MS);
    // the abort signal stays off the clean path: its request may run in another
    // realm, and the settle timer already bounds it
    if (!useClean) opts.signal = controller.signal;
    var doFetch = useClean ? cleanFetch() : function () { return window.fetch.apply(window, arguments); };
    var request;
    try {
      request = doFetch(url, opts).then(parseResponse);
    } catch (e) {
      request = Promise.reject(e);
    }
    return new Promise(function (resolve, reject) {
      var settled = false;
      var settleId = setTimeout(function () {
        if (settled) return;
        settled = true;
        if (!useClean && method === 'GET') {
          // a stalled read can be answered right away by the clean fetch; a
          // quick answer here (while the wrapped one still hangs) is what tells
          // a stuck wrapper apart from a slow network
          var startedAt = Date.now();
          fetchJSON(url, { method: 'GET' }, { clean: true }).then(function (data) {
            if (Date.now() - startedAt < QUICK_MS) goClean('a cart read stalled behind a fetch wrapper');
            resolve(data);
          }, reject);
          return;
        }
        if (!useClean) {
          // a stalled write: if it does land later, keep the drawer honest
          request.then(function () { goClean('a cart write stalled behind a fetch wrapper'); refreshDrawer().catch(function () {}); }, function () {});
        }
        reject(timeoutError(useClean ? null : request));
      }, settleMs);
      request.then(function (data) {
        clearTimeout(settleId); clearTimeout(timeoutId);
        if (settled) return;
        settled = true;
        if (useClean && method === 'POST') {
          // let the wrapper-side watchers (Redo) see that the cart changed
          try { Promise.resolve(window.fetch(ROUTES.cart, { headers: { Accept: 'application/json' } })).catch(function () {}); } catch (e) {}
        }
        resolve(data);
      }, function (err) {
        clearTimeout(settleId); clearTimeout(timeoutId);
        if (settled) return;
        settled = true;
        reject(normalizeError(err));
      });
    });
  }

  // A timed-out or dropped POST may still have reached Shopify. Re-read the cart and
  // decide from the result instead of from the error.
  function isTransientError(err) {
    return !!(err && err.transient);
  }

  // Every settled cart state is broadcast so campaign modules can react
  // without hooking the drawer internals (assets/sale-mode.js listens to
  // run the gift with purchase).
  function emitCart(cart) {
    try { document.dispatchEvent(new CustomEvent('ncd:cart', { detail: { cart: cart } })); } catch (e) {}
  }

  // ─── Cart operation queue ─────────────────────────
  var _cartQueue = Promise.resolve();
  function enqueueCartOp(fn) {
    _cartQueue = _cartQueue.then(fn, fn);
    return _cartQueue;
  }

  // ─── Drawer open/close ─────────────────────────────

  function openDrawer() {
    if (isOpen) return;
    isOpen = true;
    drawer.classList.add('is-open');
    document.body.classList.add('native-cart-open');
    // Focus trap: focus the close button
    var closeBtn = qs('.native-cart-close', drawer);
    if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 100);
  }

  function closeDrawer() {
    if (!isOpen) return;
    isOpen = false;
    drawer.classList.remove('is-open');
    document.body.classList.remove('native-cart-open');
  }

  // ─── Cart API ──────────────────────────────────────

  // Last cart state this script has seen; lets a timed-out add be reconciled.
  var lastKnownCart = null;

  function rememberCart(cart) {
    if (cart && typeof cart.item_count === 'number') lastKnownCart = cart;
    return cart;
  }

  function fetchCart() {
    // one retry for a dropped/slow read: the GET is idempotent
    return fetchJSON(ROUTES.cart).catch(function (err) {
      if (!isTransientError(err)) return Promise.reject(err);
      return fetchJSON(ROUTES.cart);
    }).then(rememberCart);
  }

  // Did the add land despite the request failing on our side? True when the cart
  // grew past what we last saw, or (no baseline) when the variant is now in it.
  function addLanded(cart, item) {
    if (!cart || !item) return false;
    var before = lastKnownCart;
    if (before && typeof before.item_count === 'number') {
      return cart.item_count >= before.item_count + (item.quantity || 1);
    }
    return cart.items.some(function (line) { return line.id === item.id; });
  }

  // Shared failure path for adds: reconcile a transient failure against the real
  // cart before deciding whether to show an error. Resolves true if the add landed.
  function recoverFailedAdd(err, item) {
    var transient = isTransientError(err);
    var pendingDone = false;
    // the stalled request may still be running: if it resolves, the add landed
    if (err && err.pending) err.pending.then(function () { pendingDone = true; }, function () {});
    var attempts = 0;
    // read with the clean fetch, and via fetchJSON rather than fetchCart so
    // lastKnownCart still holds the pre-add snapshot addLanded compares against
    function readCart() {
      var startedAt = Date.now();
      return fetchJSON(ROUTES.cart, { method: 'GET' }, { clean: true, settleMs: 5000 }).then(function (cart) {
        // a quick clean read while the wrapped write still hangs = a stuck
        // wrapper, not a slow network: stop routing through it for this visit
        if (cart && err && err.pending && !pendingDone && Date.now() - startedAt < QUICK_MS) goClean('a cart write stalled behind a fetch wrapper');
        return cart;
      }).catch(function () { return null; });
    }
    function settle() {
      if (!transient) return Promise.resolve(false);
      return readCart().then(function (cart) {
        if (pendingDone) return true;
        if (cart && addLanded(cart, item)) return true;
        attempts++;
        // a slow write can land a few seconds after our timer; give it four more reads
        if (attempts >= 4) return false;
        return new Promise(function (r) { setTimeout(r, 2000); }).then(settle);
      });
    }
    return settle().then(function (landed) {
      // refreshDrawer re-reads the cart itself; the reads above are the arbiter
      return refreshDrawer().catch(function () {}).then(function () {
        openDrawer();
        if (!landed) showCartError(err);
        return landed;
      });
    });
  }

  function addToCart(variantId, qty, properties, sellingPlan) {
    return enqueueCartOp(function () {
      var item = { id: variantId, quantity: qty || 1 };
      if (properties) item.properties = properties;
      if (sellingPlan) item.selling_plan = sellingPlan;
      drawer.classList.add('is-loading');
      return fetchJSON(ROUTES.cartAdd, {
        method: 'POST',
        body: JSON.stringify({ items: [item] })
      }).then(function () {
        return refreshDrawer();
      }).then(function () {
        openDrawer();
        drawer.classList.remove('is-loading');
      }).catch(function (err) {
        console.error('Add to cart error:', err);
        drawer.classList.remove('is-loading');
        return recoverFailedAdd(err, item);
      });
    });
  }

  // Quantity changes keep the list up (14 Oct 2026; the whole drawer used to
  // blank to the spinner): the line shows its new number straight away with its
  // stepper and remove dimmed until Shopify answers, and the footer and reward
  // bar dim as for an add (is-syncing). pendingQty keeps a line's number through
  // any other refresh that lands first.
  var pendingUpdates = 0;
  var pendingQty = {};

  function showPendingQty(key, row) {
    var q = pendingQty[key];
    if (q === undefined || !row) return;
    if (q === 0) {
      row.classList.add('is-removing');
      return;
    }
    var input = qs('input[data-action="quantity-input"]', row);
    if (input) input.value = q;
    row.classList.add('is-updating');
  }

  function releaseSync() {
    if (!pendingAdds && !pendingUpdates) drawer.classList.remove('is-syncing');
  }

  function updateQuantity(key, qty) {
    pendingQty[key] = qty;
    pendingUpdates++;
    showPendingQty(key, qs('.native-cart-item[data-key="' + key + '"]', drawer));
    drawer.classList.add('is-syncing');
    function answered() {
      if (pendingQty[key] === qty) delete pendingQty[key];
    }
    return enqueueCartOp(function () {
      return fetchJSON(ROUTES.cartChange, {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity: qty })
      }).then(function (cart) {
        answered();
        var realCount = getRealItemCount(cart) + unconfirmedQty();
        updateCartCount(realCount);
        updateSubtotal(cart.total_price);
        updateRewardBar(cart.total_price);
        updateShippingState(cart);
        updateDiscounts(cart);
        updateItems(cart);
        updateRedoSlot(cart);
        updateEmptyState(realCount);
        updateHeaderBubble(realCount);
        emitCart(cart);
      }).catch(function (err) {
        console.error('Update quantity error:', err);
        answered();
        showCartError(err);
        // put the line back to what the cart really holds
        return refreshDrawer().catch(function () {});
      }).then(function () {
        pendingUpdates = Math.max(0, pendingUpdates - 1);
        releaseSync();
      });
    });
  }

  function removeItem(key, el) {
    // Animate the item out first
    if (el) {
      var item = el.closest('.native-cart-item');
      if (item) {
        item.classList.add('is-removing');
        // Wait for animation then update
        setTimeout(function () {
          updateQuantity(key, 0);
        }, 300);
        return;
      }
    }
    updateQuantity(key, 0);
  }

  function showCartError(err) {
    var msg = (err && err.description) || (err && err.message) || 'Something went wrong. Please try again.';
    // an empty cart hides the body, so the message goes above "your cart is empty"
    var body = drawer.classList.contains('is-empty') ? qs('.native-cart-empty', drawer) : qs('.native-cart-body', drawer);
    if (!body) body = qs('.native-cart-body', drawer);
    if (!body) return;
    var existing = qs('.native-cart-error', drawer);
    if (existing) existing.remove();
    var el = document.createElement('div');
    el.className = 'native-cart-error';
    el.textContent = msg;
    body.insertBefore(el, body.firstChild);
    setTimeout(function () { if (el.parentNode) el.remove(); }, 4000);
  }

  function saveNote(text) {
    fetchJSON(ROUTES.cartUpdate, {
      method: 'POST',
      body: JSON.stringify({ note: text })
    }).catch(function (err) {
      console.error('Cart note error:', err);
    });
  }

  // ─── Redo (returns & exchanges) ────────────────────

  function isUSCustomer() {
    return drawer && drawer.getAttribute('data-country') === 'US';
  }

  function getRedoItem(cart) {
    if (!REDO_PRODUCT_ID) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].product_id === REDO_PRODUCT_ID) return cart.items[i];
    }
    return null;
  }

  function getRealItemCount(cart) {
    var count = cart.item_count;
    var redo = getRedoItem(cart);
    if (redo) count -= redo.quantity;
    return count;
  }

  // ensureRedoInCart — commented out, Redo app handles its own injection
  // function ensureRedoInCart(cart) {
  //   if (!isUSCustomer() || redoAddInProgress) return;
  //   var realCount = getRealItemCount(cart);
  //   var redo = getRedoItem(cart);
  //
  //   if (realCount > 0 && !redo) {
  //     redoAddInProgress = true;
  //     fetchJSON(ROUTES.cartAdd, {
  //       method: 'POST',
  //       body: JSON.stringify({ items: [{ id: REDO_VARIANT_ID, quantity: 1 }] })
  //     }).then(function () {
  //       redoAddInProgress = false;
  //       return refreshDrawer();
  //     }).catch(function () {
  //       redoAddInProgress = false;
  //     });
  //   } else if (realCount === 0 && redo) {
  //     fetchJSON(ROUTES.cartChange, {
  //       method: 'POST',
  //       body: JSON.stringify({ id: redo.key, quantity: 0 })
  //     }).then(function () {
  //       return refreshDrawer();
  //     });
  //   }
  // }

  function updateRedoSlot(cart) {
    if (!REDO_ENABLED) return;
    var slot = qs('#native-cart-redo-slot', drawer);
    var skipLink = qs('.native-cart-redo-skip', drawer);
    var redo = getRedoItem(cart);

    if (!redo) {
      if (slot) slot.innerHTML = '';
      if (skipLink) skipLink.style.display = 'none';
      return;
    }

    if (slot) {
      slot.innerHTML =
        '<div class="native-cart-redo-row">' +
          '<div class="native-cart-redo-row__info">' +
            '<span class="native-cart-redo-row__logo">p</span>' +
            '<div>' +
              '<span class="native-cart-redo-row__label">unlimited returns and exchanges</span>' +
              '<button type="button" class="native-cart-redo-row__tooltip" data-action="open-redo-modal" aria-label="Learn more about returns protection">&#9432;</button>' +
              '<div class="native-cart-redo-row__sub">45 day satisfaction guarantee</div>' +
            '</div>' +
          '</div>' +
          '<span class="native-cart-redo-row__price">' + formatMoney(redo.final_price) + '</span>' +
        '</div>';
    }

    if (skipLink) skipLink.style.display = '';
  }

  function checkoutWithoutRedo(cart) {
    var redo = getRedoItem(cart);
    if (!redo) {
      window.location.href = '/checkout';
      return;
    }
    fetchJSON(ROUTES.cartChange, {
      method: 'POST',
      body: JSON.stringify({ id: redo.key, quantity: 0 })
    }).then(function () {
      window.location.href = '/checkout';
    }).catch(function () {
      window.location.href = '/checkout';
    });
  }

  // ─── Upsell (Recommendations API) ─────────────────

  // One request per seed product for a minute: an add starts it on the tap
  // (Shopify takes 1-1.5s to answer it), the refresh after the add reuses it.
  var _recs = {};
  function recsFor(seedId) {
    var hit = _recs[seedId];
    if (hit && Date.now() - hit.at < 60000) return hit.promise;
    var promise = fetch('/recommendations/products.json?product_id=' + seedId + '&limit=' + Math.min(UPSELL_COUNT + 4, 16) + '&intent=related')
      .then(function (r) { return r.json(); });
    _recs[seedId] = { at: Date.now(), promise: promise };
    promise.catch(function () { delete _recs[seedId]; });
    return promise;
  }

  var upsellCall = 0;

  function updateUpsell(cart) {
    if (!UPSELL_ENABLED) return;
    var call = ++upsellCall;
    var upsellEl = qs('[data-upsell]', drawer);
    var grid = qs('[data-upsell-grid]', drawer);
    var loader = qs('[data-upsell-loader]', drawer);
    if (!upsellEl || !grid) return;

    // Find first non-Redo product in cart as the seed (never the sale mode's
    // free gift: it lands on top when it is added and would reseed the row)
    var seedId = null;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].product_id !== REDO_PRODUCT_ID && !isGwpItem(cart.items[i])) {
        seedId = cart.items[i].product_id;
        break;
      }
    }

    if (!seedId) {
      upsellEl.style.display = 'none';
      grid.innerHTML = '';
      grid.style.display = 'none';
      return;
    }

    // Already showing this product's recommendations (a quantity change, the
    // drawer reopened): keep them up rather than flashing the placeholder, and
    // redraw below only if the products to show have changed
    var showing = upsellEl.style.display !== 'none' && grid.style.display !== 'none' &&
      grid.children.length > 0 && grid.getAttribute('data-seed') === String(seedId);

    // Show upsell with loader while fetching
    if (!showing) {
      upsellEl.style.display = '';
      grid.style.display = 'none';
      grid.innerHTML = '';
      if (loader) loader.style.display = '';
    }

    // Build set of product IDs already in cart for filtering
    var cartIds = {};
    cart.items.forEach(function (item) { cartIds[item.product_id] = true; });

    recsFor(seedId)
      .then(function (data) {
        if (call !== upsellCall) return; // a newer cart is already being shown
        var products = (data.products || []).filter(function (p) {
          if (cartIds[p.id]) return false;
          if (p.id === REDO_PRODUCT_ID) return false;
          if (EXCLUDED_PRODUCT_IDS.indexOf(p.id) > -1) return false;
          if (p.handle && p.handle.toLowerCase().indexOf('redo') > -1) return false;
          if (p.vendor && p.vendor.toLowerCase().replace(/[^a-z]/g, '').indexOf('redo') > -1) return false;
          if (!p.available) return false;
          return true;
        }).slice(0, UPSELL_COUNT);

        var ids = products.map(function (p) { return p.id; }).join(',');
        if (showing && grid.getAttribute('data-ids') === ids) return;

        if (!products.length) {
          upsellEl.style.display = 'none';
          grid.innerHTML = '';
          grid.style.display = 'none';
          if (loader) loader.style.display = 'none';
          return;
        }

        var html = '';
        products.forEach(function (p) {
          var rawImg = (p.images && p.images.length > 1) ? p.images[1] : p.featured_image;
          var imgSrc = rawImg ? rawImg.replace(/(\.(jpg|jpeg|png|gif|webp))/, '_150x$1') : '';
          html += '<a href="' + p.url + '" class="native-cart-upsell__card">' +
            '<div class="native-cart-upsell__card-image">' +
              (imgSrc ? '<img src="' + imgSrc + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="75">' : '') +
            '</div>' +
            '<div class="native-cart-upsell__card-name">' + escapeHtml(p.title) + '</div>' +
            '<div class="native-cart-upsell__card-price">' + formatMoney(p.price) + '</div>' +
          '</a>';
        });

        grid.innerHTML = html;
        grid.setAttribute('data-seed', String(seedId));
        grid.setAttribute('data-ids', ids);
        if (loader) loader.style.display = 'none';
        upsellEl.style.display = '';
        grid.style.display = '';
      })
      .catch(function () {
        // a failed refresh keeps recommendations already on show
        if (showing || call !== upsellCall) return;
        upsellEl.style.display = 'none';
        grid.innerHTML = '';
        grid.style.display = 'none';
        if (loader) loader.style.display = 'none';
      });
  }

  // ─── Refresh drawer ────────────────────────────────

  var _refreshPending = null;

  function refreshDrawer() {
    if (_refreshPending) return _refreshPending;
    _refreshPending = fetchCart().then(function (cart) {
      var realCount = getRealItemCount(cart) + unconfirmedQty();
      updateCartCount(realCount);
      updateSubtotal(cart.total_price);
      updateRewardBar(cart.total_price);
      updateShippingState(cart);
      updateDiscounts(cart);
      updateItems(cart);
      updateRedoSlot(cart);
      updateUpsell(cart);
      updateEmptyState(realCount);
      updateHeaderBubble(realCount);
      emitCart(cart);
      return cart;
    }).finally(function () {
      _refreshPending = null;
    });
    return _refreshPending;
  }

  // Open the drawer on its server-rendered contents if the refresh fails: the
  // checkout button is a plain form post, so the shopper can still check out.
  function openDrawerAfterRefresh() {
    return refreshDrawer().then(openDrawer, function (err) {
      console.error('Cart refresh error:', err);
      openDrawer();
      showCartError(err);
    });
  }

  function updateCartCount(count) {
    var els = qsa(SELECTORS.cartCount, drawer);
    els.forEach(function (el) { el.textContent = '(' + count + ')'; });
  }

  function updateSubtotal(totalPrice) {
    var el = qs(SELECTORS.cartSubtotal, drawer);
    if (el) el.textContent = formatMoney(totalPrice);
  }

  function updateRewardBar(totalPrice) {
    if (!TIERS.length) return;
    var bar = qs(SELECTORS.rewardBar, drawer);
    if (!bar) return;

    var fill = qs(SELECTORS.rewardFill, bar);
    var messageEl = qs(SELECTORS.rewardMessage, bar);
    var ticks = qsa(SELECTORS.rewardTicks, bar);
    var labels = qsa(SELECTORS.rewardLabels, bar);
    var tierCount = TIERS.length;
    var segmentWidth = 100 / tierCount;

    // Determine reached tiers and fill width
    var highestReached = -1;
    var fillPct = 0;

    for (var i = 0; i < tierCount; i++) {
      if (totalPrice >= TIERS[i].threshold) {
        highestReached = i;
        fillPct = (i + 1) * segmentWidth;
      } else {
        // Partial progress within this segment
        var segStart = i === 0 ? 0 : TIERS[i - 1].threshold;
        var segEnd = TIERS[i].threshold;
        var segProgress = (totalPrice - segStart) / (segEnd - segStart);
        segProgress = Math.max(0, Math.min(1, segProgress));
        fillPct = (i * segmentWidth) + (segProgress * segmentWidth);
        break;
      }
    }

    // Update fill
    if (fill) fill.style.width = fillPct + '%';

    // Update qualified state
    var allReached = highestReached === tierCount - 1;
    if (allReached) {
      bar.classList.add('is-qualified');
    } else {
      bar.classList.remove('is-qualified');
    }

    // Update ticks and labels
    ticks.forEach(function (tick, idx) {
      if (idx <= highestReached) {
        tick.classList.add('is-reached');
      } else {
        tick.classList.remove('is-reached');
      }
    });

    labels.forEach(function (label, idx) {
      if (idx <= highestReached) {
        label.classList.add('is-reached');
      } else {
        label.classList.remove('is-reached');
      }
    });

    // Update message
    if (messageEl) {
      if (highestReached >= 0) {
        // Threshold met: "you have free shipping"; "use code:" and the copy box
        // only when the tier carries a code (sale mode tiers)
        var reachedTier = TIERS[highestReached];
        messageEl.innerHTML = 'you have <b>' + reachedTier.label + '</b>';
        if (reachedTier.code) {
          messageEl.innerHTML += ', use code:' + buildCodeBox(reachedTier.code);
        }
        // Show progress toward next tier if one exists
        var nextTier = highestReached + 1;
        if (nextTier < tierCount) {
          var nextRemaining = TIERS[nextTier].threshold - totalPrice;
          var nextRemainDollars = Math.ceil(nextRemaining / 100);
          messageEl.innerHTML += '<br><span class="reward-bar__next-hint">$' + nextRemainDollars + ' more for <b>' + TIERS[nextTier].label + '</b></span>';
        }
      } else {
        // Below first tier — show "X away from Y% off" (no code box yet)
        var remaining = TIERS[0].threshold - totalPrice;
        var remainDollars = Math.ceil(remaining / 100);
        messageEl.innerHTML = '$' + remainDollars + ' away from <b>' + TIERS[0].label + '</b>';
      }
    }
  }

  function updateDiscounts(cart) {
    var container = qs(SELECTORS.cartDiscounts, drawer);
    if (!container) {
      // Check if we need to create one
      if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
        var footer = qs('.native-cart-footer', drawer);
        if (footer) {
          var div = document.createElement('div');
          div.className = 'native-cart-footer__discounts';
          div.setAttribute('data-cart-discounts', '');
          footer.insertBefore(div, footer.firstChild);
          container = div;
        }
      }
      if (!container) return;
    }

    if (!cart.cart_level_discount_applications || cart.cart_level_discount_applications.length === 0) {
      container.innerHTML = '';
      container.style.display = 'none';
      return;
    }

    container.style.display = '';
    var html = '';
    cart.cart_level_discount_applications.forEach(function (d) {
      html += '<div class="native-cart-footer__discount-row">' +
        '<span>' + d.title + '</span>' +
        '<span>-' + formatMoney(d.total_allocated_amount) + '</span>' +
        '</div>';
    });
    container.innerHTML = html;
  }

  function updateItems(cart) {
    var container = qs(SELECTORS.cartItems, drawer);
    if (!container) return;

    // Rows already on screen (including one drawn on the tap) stay put: they keep
    // their loaded image and don't replay the slide-in animation
    var shown = {};
    var loaded = {};
    qsa('.native-cart-item', container).forEach(function (row) {
      shown[row.getAttribute('data-key')] = true;
      shown['id:' + row.getAttribute('data-item-id')] = true;
      var img = qs('.native-cart-item__image img', row);
      if (img && img.complete && img.naturalWidth) loaded[row.getAttribute('data-item-id') + '|' + imageFile(img.getAttribute('src'))] = img;
    });

    var html = '';
    cart.items.forEach(function (item) {
      if (item.product_id === REDO_PRODUCT_ID) return; // Redo rendered in footer slot
      html += buildItemHTML(item);
    });
    container.innerHTML = html;

    qsa('.native-cart-item', container).forEach(function (row) {
      if (shown[row.getAttribute('data-key')] || shown['id:' + row.getAttribute('data-item-id')]) row.classList.add('is-settled');
      var img = qs('.native-cart-item__image img', row);
      var keep = img && loaded[row.getAttribute('data-item-id') + '|' + imageFile(img.getAttribute('src'))];
      if (keep && keep !== img) img.parentNode.replaceChild(keep, img);
    });

    // adds Shopify has not answered yet keep their drawn row (see unconfirmed)
    unconfirmed.forEach(function (p) {
      if (p.isNew) {
        p.row.classList.add('is-settled');
        container.insertBefore(p.row, container.firstChild);
        return;
      }
      var row = qs('.native-cart-item[data-item-id="' + p.id + '"]:not(.native-cart-item--gift):not(.native-cart-item--gwp)', container);
      var qty = row && qs('input[data-action="quantity-input"]', row);
      if (!qty) return;
      row.setAttribute('data-ncd-qty', qty.value);
      qty.value = (parseInt(qty.value, 10) || 0) + p.qty;
      row.classList.add('is-pending');
      p.row = row;
    });

    // quantity changes Shopify has not answered yet keep their number
    Object.keys(pendingQty).forEach(function (key) {
      showPendingQty(key, qs('.native-cart-item[data-key="' + key + '"]', container));
    });
  }

  // ─── Gift card lines ───────────────────────────────
  // Mirrors snippets/pwt-gift-line.liquid (Gift Card Experience, Sep 2026): a line
  // carrying _gift_delivery shows its design, who it is for and when it sends
  // instead of one row per property, a square thumbnail and no quantity stepper.

  var GIFT_DESIGN_OPTIONS = ['color', 'design', 'occasion', 'style'];

  function isGiftItem(item) {
    return !!(item && item.properties && item.properties._gift_delivery);
  }

  function giftLines(item) {
    var p = item.properties || {};
    var lines = [];
    (item.options_with_values || []).forEach(function (opt) {
      if (opt && opt.name && opt.value && GIFT_DESIGN_OPTIONS.indexOf(opt.name.toLowerCase()) > -1) {
        lines.push('design: ' + opt.value.toLowerCase());
      }
    });
    var method = String(p._gift_delivery || '').toLowerCase();
    var methodText = method === 'sms' ? 'text' : method === 'both' ? 'email and text' : 'email';
    if (p._gift_for === 'self') {
      lines.push('for you');
      lines.push('emailed to you after checkout');
      return lines;
    }
    var to = String(p['Recipient name'] || '').trim();
    var from = String(p['From'] || '').trim();
    lines.push('to ' + (to || 'them') + (from ? ', from ' + from : ''));
    var when = String(p._gift_when || p['Delivery date'] || 'right away').toLowerCase();
    lines.push(when === 'right away' ? 'sends right away by ' + methodText : 'sends ' + when + ' by ' + methodText);
    return lines;
  }

  // Nothing ships (gift cards only): hide the free-shipping progress and say so.
  function updateShippingState(cart) {
    if (!drawer || !cart) return;
    drawer.classList.toggle('is-digital-only', cart.item_count > 0 && cart.requires_shipping === false);
  }

  // ─── Sale mode lines (oct 22 to 26, 2026) ─────────
  // Mirrors snippets/native-cart-item.liquid. The gift gift line (_gwp
  // property, managed by assets/sale-mode.js) has no quantity or remove
  // controls and shows "free" only once the admin discount zeroed it.
  // Sale-collection lines carry "exchange only" while live or
  // once discounted. window.SaleMode is inlined by snippets/sale-mode-head.
  var SM = window.SaleMode || null;

  function isGwpItem(item) {
    return !!(SM && item && item.properties && item.properties._gwp === SM.gwp.property);
  }

  function saleNoteHTML(item) {
    if (!SM || isGwpItem(item) || !SM.isSaleProduct(item.product_id)) return '';
    var discounted = item.final_price < item.original_price;
    var note = (SM.copy && SM.copy.cart_line_note) || 'exchange only';
    return '<div class="sm-line-note' + (discounted ? '' : ' sm-live-only') + '">' + escapeHtml(note) + '</div>';
  }

  function buildGwpItemHTML(item) {
    var imgHtml = item.image ? '<img src="' + getSizedImageUrl(item.image, '120x') + '" alt="' + escapeHtml(item.product_title) + '" width="52" loading="lazy">' : '';
    var priceHtml = (item.final_line_price === 0 || SM.isLive())
      ? '<span class="native-cart-item__price--original">' + formatMoney(item.original_price) + '</span><span class="native-cart-item__price--free">' + escapeHtml((SM.copy && SM.copy.cart_free) || 'free') + '</span>'
      : (item.original_price !== item.final_price
        ? '<span class="native-cart-item__price--original">' + formatMoney(item.original_price) + '</span><span class="native-cart-item__price--sale">' + formatMoney(item.final_price) + '</span>'
        : '<span>' + formatMoney(item.original_price) + '</span>');
    var sub = (item.vendor ? escapeHtml(String(item.vendor).toLowerCase()) + ' · ' : '') + escapeHtml((SM.copy && SM.copy.cart_gift_sub) || 'gift with purchase');
    var color = '';
    (item.options_with_values || []).forEach(function (opt) {
      if (opt && opt.name && /colou?r/i.test(opt.name) && opt.value) color = String(opt.value).toLowerCase();
    });
    return '<div class="native-cart-item native-cart-item--gwp" data-key="' + item.key + '" data-item-id="' + item.id + '">' +
      '<div class="native-cart-item__image"><a href="' + item.url + '">' + imgHtml + '</a></div>' +
      '<div class="native-cart-item__details">' +
        '<div class="native-cart-item__name"><a href="' + item.url + '">' + escapeHtml(item.product_title) + '</a></div>' +
        '<div class="native-cart-item__gwp-sub">' + sub + '</div>' +
        (color ? '<div class="sm-gwp-color">' + escapeHtml((SM.copy && SM.copy.cart_color_label) || 'color') + ': <span data-sm-gwp-color>' + escapeHtml(color) + '</span></div>' : '') +
        '<div class="sm-gwp-swatches" data-sm-gwp-swatches data-current="' + item.variant_id + '" data-key="' + item.key + '" role="group" aria-label="gift color"></div>' +
        '<div class="native-cart-item__bottom">' +
          '<div class="native-cart-item__price">' + priceHtml + '</div>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  function buildItemHTML(item) {
    if (isGwpItem(item)) return buildGwpItemHTML(item);
    var gift = isGiftItem(item);
    var imgHtml = '';
    if (item.image) {
      var imgSrc = item.image_loaded || cartImageUrl(item.image, '180x');
      // a row drawn on the tap loads its image straight away (lazy would wait for
      // the panel to slide into view) and holds its height while it does
      var loading = item.pending ? '' : ' loading="lazy"';
      var height = item.image_ratio ? ' height="' + Math.round(80 * item.image_ratio) + '"' : '';
      imgHtml = gift
        ? '<img src="' + imgSrc + '" alt="' + escapeHtml(item.product_title) + '" width="72" height="72"' + loading + '>'
        : '<img src="' + imgSrc + '" alt="' + escapeHtml(item.product_title) + '" width="80"' + height + loading + '>';
    }

    var variantHtml = '';
    if (item.options_with_values && item.options_with_values.length) {
      item.options_with_values.forEach(function (opt) {
        if (opt.value && opt.value !== 'Default Title') {
          variantHtml += '<div class="native-cart-item__variant">' + escapeHtml(opt.name.toLowerCase()) + ': ' + escapeHtml(opt.value.toLowerCase()) + '</div>';
        }
      });
    } else if (item.variant_title && item.variant_title !== 'Default Title') {
      variantHtml = '<div class="native-cart-item__variant">' + escapeHtml(item.variant_title) + '</div>';
    }

    var sellingPlanHtml = '';
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      sellingPlanHtml = '<div class="native-cart-item__selling-plan">' + escapeHtml(item.selling_plan_allocation.selling_plan.name) + '</div>';
    }

    var propsHtml = '';
    if (item.properties) {
      var propEntries = [];
      for (var key in item.properties) {
        if (item.properties.hasOwnProperty(key) && item.properties[key] && key.charAt(0) !== '_') {
          if (key === 'preorder') {
            propEntries.push('<span class="cart-preorder-tag">preorder · ' + escapeHtml(item.properties[key]) + '</span><br>');
          } else {
            propEntries.push('<span>' + escapeHtml(key) + ': ' + escapeHtml(item.properties[key]) + '</span><br>');
          }
        }
      }
      if (propEntries.length) {
        propsHtml = '<div class="native-cart-item__properties">' + propEntries.join('') + '</div>';
      }
    }

    var priceHtml = '';
    if (item.original_price !== item.final_price) {
      priceHtml = '<span class="native-cart-item__price--original">' + formatMoney(item.original_price) + '</span>' +
        '<span class="native-cart-item__price--sale">' + formatMoney(item.final_price) + '</span>';
    } else {
      priceHtml = '<span>' + formatMoney(item.original_price) + '</span>';
    }

    if (item.line_level_discount_allocations && item.line_level_discount_allocations.length) {
      item.line_level_discount_allocations.forEach(function (d) {
        priceHtml += '<div class="native-cart-item__line-discount">' +
          escapeHtml(d.discount_application.title) + ' (-' + formatMoney(d.amount) + ')' +
          '</div>';
      });
    }
    // Sale mode, like for like before the admin discount exists: a sale-collection
    // line that Shopify has not discounted yet shows the computed sale price.
    if (SM && SM.isLive() && SM.isSaleProduct(item.product_id) && item.original_price === item.final_price) {
      priceHtml = '<span class="native-cart-item__price--original">' + formatMoney(item.original_price) + '</span>' +
        '<span class="native-cart-item__price--sale">' + formatMoney(SM.salePrice(item.original_price)) + '</span>';
    }
    priceHtml += saleNoteHTML(item);

    if (gift) {
      variantHtml = giftLines(item).map(function (line) {
        return '<div class="native-cart-item__gift">' + escapeHtml(line) + '</div>';
      }).join('');
      sellingPlanHtml = '';
      propsHtml = '';
      return '<div class="native-cart-item native-cart-item--gift" data-key="' + item.key + '" data-item-id="' + item.id + '">' +
        '<div class="native-cart-item__image"><a href="' + item.url + '">' + imgHtml + '</a></div>' +
        '<div class="native-cart-item__details">' +
          '<div class="native-cart-item__name"><a href="' + item.url + '">' + escapeHtml(item.product_title) + '</a></div>' +
          variantHtml +
          '<div class="native-cart-item__bottom">' +
            '<span class="native-cart-item__qty-static">qty ' + item.quantity + '</span>' +
            '<div class="native-cart-item__price">' + priceHtml + '</div>' +
          '</div>' +
        '</div>' +
        '<button type="button" class="native-cart-item__remove" data-action="remove" data-key="' + item.key + '" aria-label="Remove item">' +
          '<svg viewBox="0 0 64 64"><path d="M19 17.61l27.12 27.13m0-27.12L19 44.74" stroke="currentColor" fill="none"/></svg>' +
        '</button>' +
      '</div>';
    }

    return '<div class="native-cart-item" data-key="' + item.key + '" data-item-id="' + item.id + '">' +
      '<div class="native-cart-item__image"><a href="' + item.url + '">' + imgHtml + '</a></div>' +
      '<div class="native-cart-item__details">' +
        '<div class="native-cart-item__name"><a href="' + item.url + '">' + escapeHtml(item.product_title) + '</a></div>' +
        variantHtml + sellingPlanHtml + propsHtml +
        '<div class="native-cart-item__bottom">' +
          '<div class="native-cart-qty">' +
            '<button type="button" data-action="decrease" data-key="' + item.key + '" aria-label="Decrease quantity">' +
              '<svg viewBox="0 0 20 20" fill="#444"><path d="M17.543 11.029H2.1A1.032 1.032 0 0 1 1.071 10c0-.566.463-1.029 1.029-1.029h15.443c.566 0 1.029.463 1.029 1.029 0 .566-.463 1.029-1.029 1.029z"/></svg>' +
            '</button>' +
            '<input type="number" value="' + item.quantity + '" min="1" data-key="' + item.key + '" data-action="quantity-input" aria-label="Quantity">' +
            '<button type="button" data-action="increase" data-key="' + item.key + '" aria-label="Increase quantity">' +
              '<svg viewBox="0 0 20 20" fill="#444"><path d="M17.409 8.929h-6.695V2.258c0-.566-.506-1.029-1.071-1.029s-1.071.463-1.071 1.029v6.671H1.967C1.401 8.929.938 9.435.938 10s.463 1.071 1.029 1.071h6.605V17.7c0 .566.506 1.029 1.071 1.029s1.071-.463 1.071-1.029v-6.629h6.695c.566 0 1.029-.506 1.029-1.071s-.463-1.071-1.029-1.071z"/></svg>' +
            '</button>' +
          '</div>' +
          '<div class="native-cart-item__price">' + priceHtml + '</div>' +
        '</div>' +
      '</div>' +
      '<button type="button" class="native-cart-item__remove" data-action="remove" data-key="' + item.key + '" aria-label="Remove item">' +
        '<svg viewBox="0 0 64 64"><path d="M19 17.61l27.12 27.13m0-27.12L19 44.74" stroke="currentColor" fill="none"/></svg>' +
      '</button>' +
    '</div>';
  }

  function updateEmptyState(count) {
    if (count === 0) {
      drawer.classList.add('is-empty');
      updateEmptyRecs();
    } else {
      drawer.classList.remove('is-empty');
    }
  }

  function updateEmptyRecs() {
    var recsEl = qs('[data-empty-recs]', drawer);
    var grid = qs('[data-empty-recs-grid]', drawer);
    var loader = qs('[data-empty-recs-loader]', drawer);
    if (!recsEl || !grid) return;

    var seedId = null;
    try { seedId = localStorage.getItem('pwt_last_viewed_pid'); } catch (e) {}
    if (!seedId) seedId = recsEl.getAttribute('data-fallback-product-id');
    if (!seedId) {
      recsEl.style.display = 'none';
      return;
    }

    // Show skeleton loader while fetching
    recsEl.style.display = '';
    grid.style.display = 'none';
    grid.innerHTML = '';
    if (loader) loader.style.display = '';

    fetch('/recommendations/products.json?product_id=' + seedId + '&limit=10&intent=related')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = (data.products || []).filter(function (p) {
          if (p.id === REDO_PRODUCT_ID) return false;
          if (EXCLUDED_PRODUCT_IDS.indexOf(p.id) > -1) return false;
          if (p.handle && p.handle.toLowerCase().indexOf('redo') > -1) return false;
          if (p.vendor && p.vendor.toLowerCase().replace(/[^a-z]/g, '').indexOf('redo') > -1) return false;
          if (!p.available) return false;
          return true;
        }).slice(0, 4);

        if (!products.length) {
          recsEl.style.display = 'none';
          grid.innerHTML = '';
          grid.style.display = 'none';
          if (loader) loader.style.display = 'none';
          return;
        }

        var html = '';
        products.forEach(function (p) {
          var imgSrc = p.featured_image ? getSizedImageUrl(p.featured_image, '150x') : '';
          html += '<a href="' + p.url + '" class="native-cart-empty__rec-card">' +
            '<div class="native-cart-empty__rec-card-image">' +
              (imgSrc ? '<img src="' + imgSrc + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="105">' : '') +
            '</div>' +
            '<div class="native-cart-empty__rec-card-name">' + escapeHtml(p.title) + '</div>' +
            '<div class="native-cart-empty__rec-card-price">' + formatMoney(p.price) + '</div>' +
          '</a>';
        });

        grid.innerHTML = html;
        if (loader) loader.style.display = 'none';
        grid.style.display = '';
      })
      .catch(function () {
        recsEl.style.display = 'none';
        grid.innerHTML = '';
        grid.style.display = 'none';
        if (loader) loader.style.display = 'none';
      });
  }

  function updateHeaderBubble(count) {
    var bubbles = qsa(SELECTORS.headerBubble);
    bubbles.forEach(function (el) {
      if (count > 0) {
        el.classList.add('cart-link__bubble--visible');
        el.textContent = count;
      } else {
        el.classList.remove('cart-link__bubble--visible');
        el.textContent = '';
      }
    });
  }

  // ─── Image URL helper ──────────────────────────────

  function getSizedImageUrl(src, size) {
    if (!src) return '';
    // Shopify image URL pattern: replace _WIDTHx or add before extension
    var match = src.match(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i);
    if (match) {
      var ext = match[0];
      return src.replace(ext, '_' + size + ext);
    }
    return src;
  }

  // cart.js names line images on cdn.shopify.com/s/files/1/<shop>/..., the page's
  // variant JSON on the shop's own /cdn/shop/...: the same file. Spelling both the
  // same way lets a row drawn on the tap keep its image when the confirmed row
  // replaces it, over the connection the page already has open.
  function cartImageUrl(src, size) {
    if (!src) return '';
    src = String(src)
      .replace(/^(https?:)?\/\/cdn\.shopify\.com\/s\/files\/1\/\d+\/\d+\/\d+\//, '/cdn/shop/')
      .replace(/^(https?:)?\/\/[^\/]+(\/cdn\/shop\/)/, '$2');
    return getSizedImageUrl(src, size);
  }

  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // ─── Note toggle ───────────────────────────────────

  function toggleNote() {
    var wrapper = qs(SELECTORS.noteWrapper, drawer);
    if (wrapper) wrapper.classList.toggle('is-open');
  }

  // ─── Event delegation ──────────────────────────────

  function handleDrawerClick(e) {
    var target = e.target;
    var actionEl = target.closest('[data-action]');
    if (!actionEl) return;

    var action = actionEl.getAttribute('data-action');
    var key = actionEl.getAttribute('data-key');

    switch (action) {
      case 'close-drawer':
        closeDrawer();
        break;

      case 'remove':
        e.preventDefault();
        removeItem(key, actionEl);
        break;

      case 'decrease':
        e.preventDefault();
        var input = actionEl.closest('.native-cart-qty').querySelector('input');
        var currentQty = parseInt(input.value, 10);
        if (currentQty <= 1) {
          removeItem(key, actionEl);
        } else {
          updateQuantity(key, currentQty - 1);
        }
        break;

      case 'increase':
        e.preventDefault();
        var inputPlus = actionEl.closest('.native-cart-qty').querySelector('input');
        var curQty = parseInt(inputPlus.value, 10);
        updateQuantity(key, curQty + 1);
        break;

      case 'toggle-note':
        e.preventDefault();
        toggleNote();
        break;

      case 'open-redo-modal':
        e.preventDefault();
        var modal = qs('[data-redo-modal]', drawer);
        if (modal) modal.style.display = '';
        break;

      case 'close-redo-modal':
        e.preventDefault();
        var modalClose = qs('[data-redo-modal]', drawer);
        if (modalClose) modalClose.style.display = 'none';
        break;

      case 'checkout-without-redo':
        e.preventDefault();
        fetchCart().then(function (cart) {
          checkoutWithoutRedo(cart);
        });
        break;

    }
  }

  function handleQuantityChange(e) {
    if (e.target.getAttribute('data-action') !== 'quantity-input') return;
    var key = e.target.getAttribute('data-key');
    var val = parseInt(e.target.value, 10);
    if (isNaN(val) || val < 0) return;
    if (val === 0) {
      removeItem(key, e.target);
    } else {
      updateQuantity(key, val);
    }
  }

  function handleNoteChange(e) {
    if (noteTimer) clearTimeout(noteTimer);
    noteTimer = setTimeout(function () {
      saveNote(e.target.value);
    }, 600);
  }

  // ─── Instant open ──────────────────────────────────
  // 13 Oct 2026: the drawer used to wait for the add (Shopify answers in 0.6-0.8s)
  // and then a cart re-read (~0.2s) before it started to slide in, so a tap on
  // add to bag sat for 1.2-1.6s on an iPhone (iOS Simulator, GB and US) and longer
  // on a phone network. Now the tap opens the drawer with the product already in
  // it, drawn from the buy box's own variant JSON; the confirmed cart replaces that
  // row as soon as Shopify answers. Until then the row reads "adding..." in place
  // of its price and quantity, and the footer (checkout, subtotal) waits.

  var pendingAdds = 0;

  // Sale mode: after every confirmed cart change assets/sale-mode.js adds,
  // removes or swaps the gift gift through the same queue. A tap on add to
  // bag during that would have its drawn row wiped by the gift's refresh, so
  // adds Shopify has not answered yet are kept here and redrawn (row and count)
  // by every refresh until their own POST lands.
  var unconfirmed = [];

  function unconfirmedQty() {
    var n = 0;
    unconfirmed.forEach(function (p) { n += p.qty; });
    return n;
  }

  function dropUnconfirmed(p) {
    var i = unconfirmed.indexOf(p);
    if (i > -1) unconfirmed.splice(i, 1);
  }

  function variantFromForm(form, id) {
    var ta = form.querySelector('[data-variant-json]');
    if (!ta) return null;
    if (!ta._ncdVariants) {
      try { ta._ncdVariants = JSON.parse(ta.value || ta.textContent || '[]'); } catch (e) { return null; }
    }
    var list = ta._ncdVariants;
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].id === id) return list[i];
    }
    return null;
  }

  // Option names as the picker labels them ("Size", "Color"), which is how the
  // cart names them too. Null if any is missing, or if the form sits outside a
  // product block (another product's pickers could be the ones found).
  function optionNames(form, count) {
    var scope = form.closest('.product-single__meta, .fast-meta');
    if (!scope) return null;
    var names = [];
    qsa('.variant-wrapper', scope).forEach(function (wrapper) {
      var input = qs('[data-index^="option"]', wrapper);
      var label = qs('.variant__label', wrapper);
      var idx = input ? parseInt(String(input.getAttribute('data-index')).replace('option', ''), 10) - 1 : -1;
      var text = label && label.firstChild && label.firstChild.nodeType === 3 ? label.firstChild.textContent.trim() : '';
      if (idx >= 0 && text && !names[idx]) names[idx] = text;
    });
    for (var i = 0; i < count; i++) {
      if (!names[i]) return null;
    }
    return names;
  }

  // An image of this variant the page has already loaded (the gallery shows it),
  // so the drawn row has its picture on the first frame instead of fetching one
  function loadedImage(src) {
    var file = imageFile(src);
    if (!file) return null;
    var best = null;
    qsa('img').forEach(function (img) {
      var cur = img.currentSrc || img.src;
      if (!img.complete || !img.naturalWidth || imageFile(cur) !== file) return;
      if (img.naturalWidth >= 160 && (!best || img.naturalWidth < best.naturalWidth)) best = img;
    });
    return best ? (best.currentSrc || best.src) : null;
  }

  // "ziggy_white.webp" from any spelling of a Shopify image URL
  function imageFile(src) {
    var m = String(src || '').split('?')[0].match(/([^\/]+?)(_\d*x\d*|_\{width\}x)?(\.(jpe?g|png|gif|webp))$/i);
    return m ? (m[1] + m[3]).toLowerCase() : '';
  }

  function pendingItem(form, v, item) {
    var title = v.name || '';
    var suffix = v.public_title ? ' - ' + v.public_title : '';
    if (suffix && title.slice(-suffix.length) === suffix) title = title.slice(0, -suffix.length);
    var options = (v.options || []).filter(function (o) { return o && o !== 'Default Title'; });
    var names = options.length ? optionNames(form, options.length) : null;
    return {
      pending: true,
      key: 'pending-' + v.id,
      id: v.id,
      url: (location.pathname.indexOf('/products/') > -1 ? location.pathname : '/cart') + '?variant=' + v.id,
      image: v.featured_image && v.featured_image.src,
      image_loaded: v.featured_image && loadedImage(v.featured_image.src),
      image_ratio: v.featured_image && v.featured_image.width ? v.featured_image.height / v.featured_image.width : null,
      product_title: title,
      options_with_values: names ? options.map(function (o, i) { return { name: names[i], value: o }; }) : [],
      variant_title: names ? null : (v.public_title || null),
      properties: item.properties || null,
      original_price: v.price,
      final_price: v.price,
      quantity: item.quantity
    };
  }

  function shownCount() {
    var el = qs(SELECTORS.cartCount, drawer);
    var n = el ? parseInt(String(el.textContent).replace(/\D/g, ''), 10) : NaN;
    if (!isNaN(n)) return n;
    return lastKnownCart ? getRealItemCount(lastKnownCart) : 0;
  }

  // Draw the add and open the drawer; returns its unconfirmed entry, whose row
  // is the drawn (or bumped) line. Null when the form has no variant JSON (the
  // add then runs the old way and the drawer opens when it lands).
  function showPendingAdd(form, item) {
    var container = qs(SELECTORS.cartItems, drawer);
    var v = container && variantFromForm(form, item.id);
    if (!v) return null;

    var plain = !item.properties && !item.selling_plan;
    // Shopify adds to an existing plain line in place; a new line goes on top
    var row = plain ? qs('.native-cart-item[data-item-id="' + v.id + '"]:not(.native-cart-item--gift):not(.native-cart-item--gwp)', container) : null;
    var isNew = !row;
    if (row) {
      var qty = qs('input[data-action="quantity-input"]', row);
      if (qty) {
        row.setAttribute('data-ncd-qty', qty.value);
        qty.value = (parseInt(qty.value, 10) || 0) + item.quantity;
      }
    } else {
      var holder = document.createElement('div');
      holder.innerHTML = buildItemHTML(pendingItem(form, v, item));
      row = holder.firstChild;
      container.insertBefore(row, container.firstChild);
    }
    row.classList.add('is-pending');

    var count = shownCount() + item.quantity;
    updateCartCount(count);
    updateHeaderBubble(count);
    // an estimate (dimmed) until the confirmed cart brings Shopify's own total;
    // not while the sale mode prices lines itself (assets/sale-mode.js shows a
    // simulated subtotal the estimate would not match)
    if (lastKnownCart && typeof v.price === 'number' && !(SM && SM.isLive())) {
      var estimate = lastKnownCart.total_price + v.price * item.quantity;
      updateSubtotal(estimate);
      updateRewardBar(estimate);
    }
    drawer.classList.remove('is-empty');
    drawer.classList.add('is-syncing');
    openDrawer();

    // the recommendations for this product take Shopify 1-1.5s: start them now
    var productId = v.featured_image && v.featured_image.product_id;
    if (UPSELL_ENABLED && productId) recsFor(productId).catch(function () {});
    var entry = { id: v.id, qty: item.quantity, row: row, isNew: isNew };
    unconfirmed.push(entry);
    return entry;
  }

  // The add failed and the cart could not be re-read: take the drawn row back out
  function clearPending() {
    qsa('.native-cart-item.is-pending', drawer).forEach(function (row) {
      var prev = row.getAttribute('data-ncd-qty');
      if (prev === null) {
        row.parentNode.removeChild(row);
        return;
      }
      var qty = qs('input[data-action="quantity-input"]', row);
      if (qty) qty.value = prev;
      row.removeAttribute('data-ncd-qty');
      row.classList.remove('is-pending');
    });
    if (lastKnownCart) {
      var count = getRealItemCount(lastKnownCart);
      updateCartCount(count);
      updateHeaderBubble(count);
      updateEmptyState(count);
    }
  }

  // A confirmed refresh has already replaced the drawn row. If none could be read,
  // keep the row when the add landed and take it back out when it didn't.
  function addSettled(landed) {
    pendingAdds = Math.max(0, pendingAdds - 1);
    if (pendingAdds) return;
    if (landed) {
      qsa('.native-cart-item.is-pending', drawer).forEach(function (row) {
        row.removeAttribute('data-ncd-qty');
        row.classList.remove('is-pending');
      });
    } else if (qs('.native-cart-item.is-pending', drawer)) {
      clearPending();
    }
    releaseSync();
  }

  // Bring a line into view in the drawer body. The top line shows from the very
  // top (reward bar included); a line further down, which is where Shopify adds
  // to an existing line, scrolls just enough to be whole, REVEAL_GAP clear of
  // the bottom edge. Returns the scroll position it left, so a later call can
  // tell whether the shopper scrolled.
  var REVEAL_GAP = 16;
  function revealRow(row) {
    var body = qs('.native-cart-body', drawer);
    if (!body) return null;
    if (!row || !body.contains(row) || row === qs('.native-cart-item', body)) {
      if (body.scrollTop) body.scrollTop = 0;
      return body.scrollTop;
    }
    var b = body.getBoundingClientRect();
    var r = row.getBoundingClientRect();
    if (r.top < b.top) body.scrollTop -= b.top - r.top;
    else if (r.bottom + REVEAL_GAP > b.bottom) body.scrollTop += Math.min(r.bottom + REVEAL_GAP - b.bottom, r.top - b.top);
    return body.scrollTop;
  }

  // After an add the body can still shrink for a moment (the recommendations grid
  // replacing its placeholder, Redo's checkout row arriving), which would cut off
  // a line at the end of the list. For a few seconds, re-reveal it on every
  // resize, until the shopper scrolls.
  var holdObserver = null;
  function holdInView(id, at) {
    var body = qs('.native-cart-body', drawer);
    if (holdObserver) { holdObserver.disconnect(); holdObserver = null; }
    if (!body || typeof ResizeObserver !== 'function') return;
    var until = Date.now() + 4000;
    var observer = holdObserver = new ResizeObserver(function () {
      if (Date.now() > until || body.scrollTop !== at || !isOpen) {
        observer.disconnect();
        if (holdObserver === observer) holdObserver = null;
        return;
      }
      at = revealRow(qs('.native-cart-item[data-item-id="' + id + '"]:not(.native-cart-item--gift):not(.native-cart-item--gwp)', drawer));
    });
    observer.observe(body);
  }

  // Run fn once the open drawer has been handed to the screen. The fetch wrappers
  // on the add (Redo, Shopify's event listener and pixels) held the main thread
  // for up to 240ms after the tap in the simulator, more on a phone; starting the
  // add after that first frame keeps the slide-in from waiting behind them.
  function afterPaint(fn) {
    var done = false;
    function go() { if (!done) { done = true; fn(); } }
    requestAnimationFrame(function () { setTimeout(go, 0); });
    setTimeout(go, 50); // no frames in a background tab
  }

  // ─── Intercept add-to-cart forms ───────────────────

  function handleAddToCartForm(form) {
      var formData = new FormData(form);
      var item = {};

      // Extract variant ID
      var idInput = form.querySelector('[name="id"]');
      if (idInput) item.id = parseInt(idInput.value, 10);

      // Extract quantity
      var qtyInput = form.querySelector('[name="quantity"]');
      item.quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;

      // Extract properties
      var props = {};
      formData.forEach(function (val, key) {
        if (key.startsWith('properties[')) {
          var propKey = key.replace('properties[', '').replace(']', '');
          props[propKey] = val;
        }
      });
      if (Object.keys(props).length) item.properties = props;

      // Extract selling plan
      var sellingPlan = formData.get('selling_plan');
      if (sellingPlan) item.selling_plan = sellingPlan;

      if (!item.id) return;

      // Prevent double-submit
      if (form.getAttribute('data-ncd-adding')) return;
      form.setAttribute('data-ncd-adding', 'true');

      // Show loading state on button — target inner text span to preserve DOM structure
      var submitBtn = form.querySelector('[type="submit"]');
      var textSpan = submitBtn ? submitBtn.querySelector('[data-add-to-cart-text]') : null;
      var textEl = textSpan || submitBtn;
      var originalDefaultText = textSpan ? textSpan.getAttribute('data-default-text') : null;

      // IMPORTANT: Do NOT set submitBtn.disabled = true — that triggers the
      // theme CSS rule `.add-to-cart[disabled] ~ .notify-wrapper` which shows
      // the "notify me" button. Instead use is-loading class + form flag.
      if (submitBtn) submitBtn.classList.add('is-loading');

      // Swap data-default-text so theme.js updateCartButton (which reads
      // dataset.defaultText and writes it to textContent) reinforces our state
      if (textSpan) textSpan.setAttribute('data-default-text', 'adding...');
      if (textEl) textEl.textContent = 'adding...';

      // Hide the notify wrapper for the duration of our flow
      var notifyWrapper = form.parentElement ? form.parentElement.querySelector('.notify-wrapper') : null;
      if (!notifyWrapper) notifyWrapper = form.closest('.product-block') ? form.closest('.product-block').querySelector('.notify-wrapper') : null;
      if (notifyWrapper) notifyWrapper.style.display = 'none';

      var pending = showPendingAdd(form, item);
      var instant = !!pending;
      if (instant) pendingAdds++;

      function restoreButton(showAdded) {
        if (submitBtn) submitBtn.classList.remove('is-loading');
        if (textSpan && originalDefaultText) textSpan.setAttribute('data-default-text', originalDefaultText);
        if (showAdded && textEl) {
          textEl.textContent = 'added!';
          if (textSpan) textSpan.setAttribute('data-default-text', 'added!');
          setTimeout(function () {
            if (textEl) textEl.textContent = originalDefaultText || 'add to bag';
            if (textSpan && originalDefaultText) textSpan.setAttribute('data-default-text', originalDefaultText);
            if (notifyWrapper) notifyWrapper.style.display = '';
            form.removeAttribute('data-ncd-adding');
          }, 1500);
        } else {
          if (textEl && originalDefaultText) textEl.textContent = originalDefaultText;
          if (notifyWrapper) notifyWrapper.style.display = '';
          form.removeAttribute('data-ncd-adding');
        }
      }

      // The line being added is in view on the drawer's very first frame: that
      // frame lays out anyway, so measuring in its animation callback costs
      // nothing (in the tap itself it would force an extra layout)
      var revealedAt;
      if (instant) requestAnimationFrame(function () { if (revealedAt === undefined) revealedAt = revealRow(pending.row); });

      function send() {
        if (instant && revealedAt === undefined) revealedAt = revealRow(pending.row); // no frames in a background tab
        suppressThirdPartyDrawers();
        enqueueCartOp(function () {
          return fetchJSON(ROUTES.cartAdd, {
            method: 'POST',
            body: JSON.stringify({ items: [item] })
          }).then(function () {
            dropUnconfirmed(pending);
            return refreshDrawer();
          }).then(function () {
            openDrawer();
            if (instant) {
              // the confirmed line, and the recommendations row that squeezes
              // the body, can push it out of view: bring it back unless the
              // shopper has scrolled since
              var body = qs('.native-cart-body', drawer);
              if (body && body.scrollTop === revealedAt) {
                holdInView(item.id, revealRow(qs('.native-cart-item[data-item-id="' + item.id + '"]:not(.native-cart-item--gift):not(.native-cart-item--gwp)', drawer)));
              }
              addSettled(true);
            }
            restoreButton(true);
          }).catch(function (err) {
            console.error('Add to cart error:', err);
            dropUnconfirmed(pending);
            return recoverFailedAdd(err, item).then(function (landed) {
              if (instant) addSettled(!!landed);
              restoreButton(!!landed);
            });
          });
        });
      }

      if (instant) afterPaint(send);
      else send();
  }

  function interceptAddToCart() {
    // Capture-phase click handler — fires before any other click handler
    // Needed because something (app/script) calls preventDefault on button clicks,
    // preventing the native form submit from ever firing
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('form[action*="/cart/add"] [type="submit"]');
      if (!btn) return;
      var form = btn.closest('form[action*="/cart/add"]');
      if (!form) return;
      if (e.ctrlKey || e.metaKey) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      handleAddToCartForm(form);
    }, true);

    // Also keep submit listener as a fallback (e.g. programmatic submits)
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form.matches || !form.matches('form[action*="/cart/add"]')) return;

      if (e.ctrlKey || e.metaKey) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      handleAddToCartForm(form);
    }, true);
  }

  // ─── Suppress third-party cart drawers ───────────────

  function nukeSlideCart() {
    var killed = false;
    qsa('#slidecarthq, .slidecarthq, .slidecarthq-overlay, [id*="slidecart"], [class*="slidecart"]').forEach(function (el) {
      el.remove();
      killed = true;
    });
    return killed;
  }

  function suppressThirdPartyDrawers() {
    nukeSlideCart();

    // Hide Archetype built-in drawer if present
    var archetypeDrawer = qs('#CartDrawer');
    if (archetypeDrawer) {
      archetypeDrawer.style.cssText = 'display:none!important;visibility:hidden!important;';
    }
  }

  // Persistent MutationObserver — removes SlideCart the instant it appears
  function watchForSlideCart() {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          // typeof guard: a form containing <input name="id"> shadows node.id with the element itself
          if (typeof node.id === 'string' && node.id.toLowerCase().indexOf('slidecart') > -1 ||
              node.className && typeof node.className === 'string' && node.className.toLowerCase().indexOf('slidecart') > -1) {
            node.remove();
            continue;
          }
          // Check children in case SlideCart is nested inside a wrapper
          var nested = node.querySelectorAll ? node.querySelectorAll('[id*="slidecart"], [class*="slidecart"]') : [];
          for (var k = 0; k < nested.length; k++) { nested[k].remove(); }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ─── Cart icon click ───────────────────────────────

  function bindCartIconLinks() {
    // Delegate on document for both original and sticky header clones
    document.addEventListener('click', function (e) {
      var cartLink = e.target.closest('.js-native-cart-open');
      if (!cartLink) return;

      e.preventDefault();
      suppressThirdPartyDrawers();

      if (isOpen) {
        closeDrawer();
      } else {
        openDrawerAfterRefresh();
      }
    });
  }

  // ─── Keyboard ──────────────────────────────────────

  function handleKeydown(e) {
    if (e.key === 'Escape' && isOpen) {
      closeDrawer();
    }
  }

  // ─── Init ──────────────────────────────────────────

  function init() {
    drawer = qs(SELECTORS.drawer);
    if (!drawer) return;

    // Read configuration from data attributes (set by section settings)
    try { TIERS = JSON.parse(drawer.getAttribute('data-tiers') || '[]'); } catch (e) { TIERS = []; }
    REDO_ENABLED = drawer.getAttribute('data-redo-enable') === 'true';
    REDO_PRODUCT_ID = parseInt(drawer.getAttribute('data-redo-product-id'), 10) || 9923270607158;
    REDO_VARIANT_ID = parseInt(drawer.getAttribute('data-redo-variant-id'), 10) || 0;
    UPSELL_ENABLED = drawer.getAttribute('data-upsell-enable') === 'true';
    UPSELL_COUNT = parseInt(drawer.getAttribute('data-upsell-count'), 10) || 4;
    NOTE_ENABLED = drawer.getAttribute('data-note-enable') === 'true';

    // Event delegation on drawer
    drawer.addEventListener('click', handleDrawerClick);
    drawer.addEventListener('change', handleQuantityChange);

    // Note debounced save
    var noteInput = qs(SELECTORS.noteInput, drawer);
    if (noteInput) noteInput.addEventListener('input', handleNoteChange);

    // Nuke SlideCart and watch for it permanently
    suppressThirdPartyDrawers();
    watchForSlideCart();

    // Cart icon links
    bindCartIconLinks();

    // Intercept add-to-cart forms
    interceptAddToCart();

    // Read the buy box's variant JSON (~150KB on big PDPs) when a finger lands on
    // add to bag, not in the tap itself
    function prewarm(e) {
      var btn = e.target && e.target.closest && e.target.closest('form[action*="/cart/add"] [type="submit"]');
      if (btn) variantFromForm(btn.closest('form[action*="/cart/add"]'), 0);
    }
    document.addEventListener('touchstart', prewarm, { capture: true, passive: true });
    document.addEventListener('pointerdown', prewarm, { capture: true, passive: true });

    // Escape key
    document.addEventListener('keydown', handleKeydown);

    // Track last viewed product for empty cart recs
    if (window.meta && window.meta.product && window.meta.product.id) {
      try { localStorage.setItem('pwt_last_viewed_pid', window.meta.product.id); } catch (e) {}
    }

    // Initialize reward bar from current cart
    fetchCart().then(function (cart) {
      var realCount = getRealItemCount(cart) + unconfirmedQty();
      updateRewardBar(cart.total_price);
      updateShippingState(cart);
      updateHeaderBubble(realCount);
      updateCartCount(realCount);
      emitCart(cart);
    }).catch(function (err) {
      console.error('Cart init error:', err);
    });
  }

  // Add without opening the drawer (campaign auto-adds such as the sale
  // mode gift); rejects on a failed add so the caller can back off.
  function addQuiet(variantId, qty, properties) {
    return enqueueCartOp(function () {
      var item = { id: variantId, quantity: qty || 1 };
      if (properties) item.properties = properties;
      return fetchJSON(ROUTES.cartAdd, {
        method: 'POST',
        body: JSON.stringify({ items: [item] })
      }).then(function () {
        return refreshDrawer();
      });
    });
  }

  // ─── Public API ────────────────────────────────────

  window.NativeCart = {
    open: function () {
      suppressThirdPartyDrawers();
      if (isOpen) { closeDrawer(); return; }
      openDrawerAfterRefresh();
    },
    close: function () { closeDrawer(); },
    refresh: function () { return refreshDrawer(); },
    add: function (variantId, qty, props, sellingPlan) { return addToCart(variantId, qty, props, sellingPlan); },
    addQuiet: function (variantId, qty, props) { return addQuiet(variantId, qty, props); },
    change: function (key, qty) { return updateQuantity(key, qty); },
    fetch: function () { return fetchCart(); },
    // Re-read reward tiers from data-tiers and re-render the reward bar.
    // Used by the shipping-threshold A/B snippet after it overrides data-tiers.
    applyTiers: function () {
      if (!drawer) return;
      try { TIERS = JSON.parse(drawer.getAttribute('data-tiers') || '[]'); } catch (e) { TIERS = []; }
      fetchCart().then(function (cart) { updateRewardBar(cart.total_price); }).catch(function () {});
    }
  };

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
